import { setAuthHeaderProvider } from "@src/appV2/Auth/cognito/authHeaderProviderRef";
import { getCognitoIdTokenWithProactiveRefresh } from "@src/appV2/Auth/cognito/getCognitoIdTokenWithProactiveRefresh";
import constate from "constate";
import { useCallback, useEffect } from "react";

const BEARER_TOKEN_PREFIX = "Bearer ";

/**
 * Hook that provides centralized authentication header values for API calls.
 * Always uses Cognito as the sole authentication provider.
 *
 * Returns values ready for the Authorization header: `Bearer <token>`
 *
 * @returns An object with getAuthHeaderValue function that returns the formatted auth value
 */
function useAuthHeaderProvider() {
  const getAuthHeaderValue = useCallback(
    async (forceRefresh?: boolean): Promise<string | undefined> => {
      try {
        const token = await getCognitoIdTokenWithProactiveRefresh(forceRefresh);
        if (token) {
          return `${BEARER_TOKEN_PREFIX}${token}`;
        }

        return undefined;
      } catch {
        throw new Error("Unable to get user token");
      }
    },
    []
  );

  useEffect(() => {
    setAuthHeaderProvider(getAuthHeaderValue);
    return () => {
      setAuthHeaderProvider(undefined);
    };
  }, [getAuthHeaderValue]);

  return { getAuthHeaderValue };
}

export const [AuthHeaderProvider, useAuthHeaderContext] = constate(useAuthHeaderProvider);
